/* Scroll scrub React/TanStack reference implementation. */

import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";

import "./scroll-scrub.css";

export interface ScrollScrubScene {
  id: string;
  label: string;
  /** Exact first frame of the deployed desktop clip. */
  poster: string;
  /** Exact first frame of mobileClip; provide whenever mobileClip is set. */
  mobilePoster?: string;
  clip: string;
  mobileClip?: string;
  title: string;
  body: string;
  kicker?: string;
  tags?: string[];
  actions?: ReactNode;
  align?: "left" | "right";
  /** Viewport-heights assigned to this scene. More distance means slower scrub. */
  scroll?: number;
  /** 0..0.6. Slow the middle of the clip without changing either seam frame. */
  linger?: number;
  objectPosition?: string;
  mobileObjectPosition?: string;
  /** Segment progress window [from, to] over which the copy fades out. */
  copyFade?: [number, number];
  /** Short line shown late in the scene, centred under the picture. */
  epilogue?: string;
  /** Segment progress [inFrom, inTo, outFrom, outTo] for the epilogue. */
  epilogueAt?: [number, number, number, number];
}

export interface ScrollScrubConnector {
  /** Exact first frame of this connector clip; never substitute a scene still. */
  poster: string;
  /** Exact first frame of mobileClip; provide whenever mobileClip is set. */
  mobilePoster?: string;
  clip: string;
  mobileClip?: string;
  scroll?: number;
}

export interface ScrollScrubTheme {
  background: string;
  ink: string;
  muted: string;
  accent: string;
}

export interface ScrollScrubProps {
  chaptersLabel?: string;
  scenes: ScrollScrubScene[];
  /** Leave empty for continuous-forward architecture A. */
  connectors?: (ScrollScrubConnector | null)[];
  theme: ScrollScrubTheme;
  className?: string;
  onActiveSectionChange?: (index: number) => void;
}

interface Segment {
  key: string;
  kind: "scene" | "connector";
  sectionIndex: number;
  nextSectionIndex: number;
  poster: string;
  mobilePoster?: string;
  clip: string;
  mobileClip?: string;
  weight: number;
  linger: number;
  objectPosition: string;
  mobileObjectPosition: string;
  scene?: ScrollScrubScene;
}

interface RuntimeSegment extends Segment {
  band: HTMLElement;
  layer: HTMLElement;
  start: number;
  end: number;
  current: number;
  target: number;
  visible: boolean;
  loading: boolean;
  ready: boolean;
  failed: boolean;
  loadedSource?: string;
  video?: HTMLVideoElement;
  objectUrl?: string;
  abort?: AbortController;
  isSeeking?: boolean;
  lastSeek?: number;
  lastOpacity?: number;
  lastZ?: string;
  lastCopy?: string;
  lastEpi?: string;
}

interface Controller {
  jumpToSection: (index: number) => void;
}

type ThemeStyle = CSSProperties & Record<`--ss-${string}`, string | number>;

const clamp = (value: number, min = 0, max = 1) =>
  Math.min(max, Math.max(min, value));

const smoothstep = (value: number) => {
  const x = clamp(value);
  return x * x * (3 - 2 * x);
};

const lingerEase = (value: number, amount: number) => {
  const x = clamp(value);
  const linger = clamp(amount, 0, 0.6);
  const centered = x - 0.5;
  return (1 - linger) * x + linger * (4 * centered ** 3 + 0.5);
};

function buildSegments(
  scenes: ScrollScrubScene[],
  connectors: (ScrollScrubConnector | null)[]
): Segment[] {
  const result: Segment[] = [];

  for (const [index, scene] of scenes.entries()) {
    if (scene.mobileClip && !scene.mobilePoster) {
      throw new Error(`Scene ${scene.id} needs mobilePoster for mobileClip`);
    }
    result.push({
      clip: scene.clip,
      key: `scene:${scene.id}`,
      kind: "scene",
      linger: scene.linger ?? 0,
      mobileClip: scene.mobileClip,
      mobilePoster: scene.mobilePoster,
      mobileObjectPosition:
        scene.mobileObjectPosition ?? scene.objectPosition ?? "50% 50%",
      nextSectionIndex: index,
      objectPosition: scene.objectPosition ?? "50% 50%",
      poster: scene.poster,
      scene,
      sectionIndex: index,
      weight: scene.scroll ?? 1.4,
    });

    const connector = connectors[index];
    if (index < scenes.length - 1 && connector?.clip) {
      if (connector.mobileClip && !connector.mobilePoster) {
        throw new Error(
          `Connector after ${scene.id} needs mobilePoster for mobileClip`
        );
      }
      const nextScene = scenes[index + 1];
      result.push({
        clip: connector.clip,
        key: `connector:${scene.id}:${nextScene.id}`,
        kind: "connector",
        linger: 0,
        mobileClip: connector.mobileClip,
        mobilePoster: connector.mobilePoster,
        mobileObjectPosition:
          nextScene.mobileObjectPosition ??
          nextScene.objectPosition ??
          "50% 50%",
        nextSectionIndex: index + 1,
        objectPosition: nextScene.objectPosition ?? "50% 50%",
        poster: connector.poster,
        sectionIndex: index,
        weight: connector.scroll ?? 0.8,
      });
    }
  }

  return result;
}

export function ScrollScrub({
  scenes,
  connectors,
  theme,
  className,
  onActiveSectionChange,
  chaptersLabel = "Chapters",
}: ScrollScrubProps) {
  const rootRef = useRef<HTMLElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const controllerRef = useRef<Controller | null>(null);
  const onActiveRef = useRef(onActiveSectionChange);
  const [activeSection, setActiveSection] = useState(0);
  const segments = useMemo(
    () => buildSegments(scenes, connectors ?? []),
    [connectors, scenes]
  );

  // Keep the latest callback reachable from the scroll loop without making it a
  // dependency of the controller effect. Synced in an effect, never during
  // render — a render-phase ref write breaks under React Compiler.
  useEffect(() => {
    onActiveRef.current = onActiveSectionChange;
  }, [onActiveSectionChange]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || segments.length === 0) {
      return;
    }

    const layerNodes = [
      ...root.querySelectorAll<HTMLElement>("[data-scroll-scrub-layer]"),
    ];
    const bandNodes = [
      ...root.querySelectorAll<HTMLElement>("[data-scroll-scrub-band]"),
    ];
    if (
      layerNodes.length !== segments.length ||
      bandNodes.length !== segments.length
    ) {
      throw new Error("ScrollScrub segment markup is out of sync");
    }

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    const coarsePointer = window.matchMedia(
      "(hover: none) and (pointer: coarse)"
    ).matches;
    const smallViewport = window.matchMedia("(max-width: 860px)");
    const isMobile = () =>
      window.innerWidth < 768 || coarsePointer || smallViewport.matches;
    const sourceFor = (segment: RuntimeSegment) =>
      isMobile() && segment.mobileClip ? segment.mobileClip : segment.clip;
    const runtime: RuntimeSegment[] = segments.map((segment, index) => ({
      ...segment,
      band: bandNodes[index],
      current: 0,
      end: 0,
      failed: false,
      layer: layerNodes[index],
      loading: false,
      ready: false,
      start: 0,
      target: 0,
      visible: index === 0,
    }));

    let active = -1;
    let destroyed = false;
    let dirty = true;
    let frame = 0;
    let rootTop = 0;
    let total = 1;
    let viewportHeight = window.innerHeight;
    let layoutWidth = window.innerWidth;
    let userReady = false;
    let canLoadClips = !isMobile();

    // Canvas Frame Sequence Engine for Mobile
    const canvas = canvasRef.current;
    let ctx: CanvasRenderingContext2D | null = null;
    if (canvas) {
      ctx = canvas.getContext("2d", { alpha: false });
    }

    let canvasDirty = true;
    const FRAMES_PER_SCENE = 60;
    const imageCache = new Map<string, HTMLImageElement>();
    const preloadedScenes = new Set<number>();
    // Frames are only drawn once decoded off the main thread: drawing a merely
    // "complete" image makes iOS Safari decode it synchronously mid-scroll.
    const decoded = new WeakSet<HTMLImageElement>();
    const isDrawable = (img: HTMLImageElement) =>
      decoded.has(img) && img.naturalWidth > 0;

    const getImage = (sceneIdx: number, frameIdx: number): HTMLImageElement => {
      const f = clamp(frameIdx, 0, FRAMES_PER_SCENE - 1) + 1;
      const s = sceneIdx + 1;
      const key = `${s}_${f}`;
      let img = imageCache.get(key);
      if (!img) {
        img = new Image();
        img.decoding = "async";
        if (s === 1 && f === 1) {
          img.src = "/assets/world/scene-01-mobile-poster.webp";
        } else {
          img.src = `/assets/world/scene-0${s}-frames/frame-${String(f).padStart(3, "0")}.webp`;
        }
        const loaded = img;
        loaded.onload = () => {
          const markReady = () => {
            if (imageCache.get(key) !== loaded) return;
            decoded.add(loaded);
            canvasDirty = true;
          };
          if (typeof loaded.decode === "function") {
            loaded.decode().then(markReady, markReady);
          } else {
            markReady();
          }
        };
        imageCache.set(key, img);
      }
      return img;
    };

    // Priority-based non-blocking batch preloading via requestIdleCallback
    const preloadScene = (sceneIdx: number) => {
      if (preloadedScenes.has(sceneIdx) || sceneIdx < 0 || sceneIdx >= scenes.length) {
        return;
      }
      preloadedScenes.add(sceneIdx);

      const loadBatch = (startIndex: number) => {
        if (destroyed || !preloadedScenes.has(sceneIdx)) return;
        const endIndex = Math.min(startIndex + 6, FRAMES_PER_SCENE);
        for (let f = startIndex; f < endIndex; f++) {
          getImage(sceneIdx, f);
        }
        if (endIndex < FRAMES_PER_SCENE) {
          if (typeof window !== "undefined" && "requestIdleCallback" in window) {
            (window as any).requestIdleCallback(() => loadBatch(endIndex), { timeout: 120 });
          } else {
            setTimeout(() => loadBatch(endIndex), 16);
          }
        }
      };

      loadBatch(0);
    };

    // Garbage-collection friendly memory manager: evicts distant scenes to protect mobile RAM
    const evictScene = (sceneIdx: number) => {
      const s = sceneIdx + 1;
      for (let f = 1; f <= FRAMES_PER_SCENE; f++) {
        if (s === 1 && f === 1) continue; // Keep hero frame permanent
        const key = `${s}_${f}`;
        const img = imageCache.get(key);
        if (img) {
          img.onload = null;
          img.onerror = null;
          img.src = ""; // Signals WebKit/Blink to release GPU memory immediately
          imageCache.delete(key);
        }
      }
      preloadedScenes.delete(sceneIdx);
    };

    const manageMobileMemory = (activeSceneIndex: number) => {
      if (!isMobile()) return;

      const keepScenes = new Set<number>();
      keepScenes.add(activeSceneIndex);
      if (activeSceneIndex > 0) keepScenes.add(activeSceneIndex - 1);
      if (activeSceneIndex < scenes.length - 1) keepScenes.add(activeSceneIndex + 1);

      // Fetch only the scene in view and the one ahead; the previous scene is
      // kept if already cached but never re-fetched (saves data on 3G/4G).
      preloadScene(activeSceneIndex);
      if (activeSceneIndex < scenes.length - 1) {
        preloadScene(activeSceneIndex + 1);
      }

      for (const loaded of Array.from(preloadedScenes)) {
        if (!keepScenes.has(loaded)) {
          evictScene(loaded);
        }
      }
    };

    const drawSceneToCanvas = (
      c: CanvasRenderingContext2D,
      seg: RuntimeSegment,
      cw: number,
      ch: number,
      alpha: number
    ) => {
      const sIdx = seg.sectionIndex;
      const progress = clamp(seg.current !== undefined ? seg.current : seg.target, 0, 1);
      const targetFrame = clamp(
        Math.round(progress * (FRAMES_PER_SCENE - 1)),
        0,
        FRAMES_PER_SCENE - 1
      );
      let img = getImage(sIdx, targetFrame);

      if (!isDrawable(img)) {
        // Look only at frames already requested; creating them here would
        // fetch the whole scene before the visitor has scrolled.
        const peek = (frameIdx: number) => {
          if (frameIdx < 0 || frameIdx >= FRAMES_PER_SCENE) return undefined;
          const cached = imageCache.get(`${sIdx + 1}_${frameIdx + 1}`);
          return cached && isDrawable(cached) ? cached : undefined;
        };
        for (let offset = 1; offset < FRAMES_PER_SCENE; offset++) {
          const near = peek(targetFrame - offset) ?? peek(targetFrame + offset);
          if (near) {
            img = near;
            break;
          }
        }
      }
      if (!isDrawable(img)) {
        img = getImage(sIdx, 0);
        if (!isDrawable(img)) {
          img = getImage(0, 0);
        }
      }

      if (isDrawable(img)) {
        c.globalAlpha = clamp(alpha, 0, 1);
        const iw = img.naturalWidth;
        const ih = img.naturalHeight;
        const r = Math.max(cw / iw, ch / ih);
        const nw = iw * r;
        const nh = ih * r;
        const cx = (cw - nw) * 0.5;
        const cy = (ch - nh) * 0.5;
        c.drawImage(img, cx, cy, nw, nh);
        c.globalAlpha = 1.0;
        if (canvas && !canvas.dataset.ready) {
          canvas.dataset.ready = "true";
        }
      }
    };

    const renderCanvas = () => {
      if (!canvas || !ctx || !isMobile() || !canvasDirty) {
        return;
      }

      // Frames are 540px wide, so a backing store above ~1.5x only costs GPU
      // memory (a 3x iPhone canvas would be ~7.6 MB) without adding detail.
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      const cw = Math.round((canvas.clientWidth || window.innerWidth) * dpr);
      const ch = Math.round((canvas.clientHeight || window.innerHeight) * dpr);
      if (canvas.width !== cw || canvas.height !== ch) {
        canvas.width = cw;
        canvas.height = ch;
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
      }

      const visibleScenes = runtime.filter(
        (s) => s.kind === "scene" && s.visible
      );
      if (visibleScenes.length === 0) {
        const s0 = runtime.find((s) => s.kind === "scene") || runtime[0];
        drawSceneToCanvas(ctx, s0, cw, ch, 1.0);
        canvasDirty = false;
        return;
      }

      if (visibleScenes.length === 1) {
        drawSceneToCanvas(ctx, visibleScenes[0], cw, ch, 1.0);
        canvasDirty = false;
        return;
      }

      // Crossfade: Base scene drawn solid, next scene blended smoothly on top
      drawSceneToCanvas(ctx, visibleScenes[0], cw, ch, 1.0);
      for (let i = 1; i < visibleScenes.length; i++) {
        const seg = visibleScenes[i];
        const opacity =
          seg.lastOpacity !== undefined ? seg.lastOpacity : 1.0;
        drawSceneToCanvas(ctx, seg, cw, ch, opacity);
      }
      canvasDirty = false;
    };

    // Preload the hero frame immediately; the tick loop paints it once decoded.
    const heroImg = getImage(0, 0);
    if (heroImg.complete && heroImg.naturalWidth > 0) {
      heroImg.onload?.(new Event("load"));
    }

    const enableClips = () => {
      if (canLoadClips || destroyed) return;
      canLoadClips = true;
      dirty = true;
      canvasDirty = true;
      if (isMobile()) {
        manageMobileMemory(0);
      }
    };

    if (typeof window !== "undefined") {
      if (isMobile()) {
        // Frame sequences (~2 MB per scene) load on the first gesture only, so
        // visitors who never scroll the film don't pay for it on mobile data.
        window.addEventListener("touchstart", enableClips, {
          once: true,
          passive: true,
        });
        window.addEventListener("scroll", enableClips, {
          once: true,
          passive: true,
        });
        window.addEventListener("pointerdown", enableClips, {
          once: true,
          passive: true,
        });
      } else {
        canLoadClips = true;
      }
    }

    const unloadClip = (segment: RuntimeSegment) => {
      segment.video?.remove();
      delete segment.video;
      delete segment.loadedSource;
      segment.loading = false;
      segment.ready = false;
      segment.failed = false;
      segment.current = segment.target;
      delete segment.layer.dataset.videoPainted;
      delete segment.layer.dataset.videoFailed;
    };

    const layout = () => {
      const pageY = window.scrollY || window.pageYOffset;
      rootTop = root.getBoundingClientRect().top + pageY;
      viewportHeight = window.innerHeight;
      layoutWidth = window.innerWidth;
      const mobile = isMobile();

      let accumulated = 0;
      for (const segment of runtime) {
        if (
          segment.loadedSource &&
          segment.loadedSource !== sourceFor(segment)
        ) {
          unloadClip(segment);
        }
        const effectiveWeight = mobile
          ? (segment.kind === "scene" ? Math.max(segment.weight * 0.75, 1.4) : 0.0)
          : segment.weight;
        const bandHeight = effectiveWeight * viewportHeight;
        segment.start = accumulated;
        segment.end = accumulated + bandHeight;
        accumulated += bandHeight;
      }
      total = Math.max(accumulated, viewportHeight);
      dirty = true;
    };

    let lastProgress = -1;

    const primeVideo = async (video?: HTMLVideoElement) => {
      if (!video) {
        return;
      }
      try {
        const p = video.play();
        if (p !== undefined) {
          await p;
          video.pause();
        }
      } catch {
        // Keep the poster; a later seek will render naturally.
      }
    };

    // DESKTOP: Precise scrub-to-scroll clip loader
    const loadClip = (segment: RuntimeSegment) => {
      if (isMobile()) {
        return; // Mobile uses Canvas sequence, no video element needed!
      }
      const source = segment.clip;
      if (
        reduceMotion ||
        destroyed ||
        segment.loading ||
        segment.ready ||
        segment.failed ||
        !source ||
        segment.video
      ) {
        return;
      }

      segment.loading = true;
      segment.loadedSource = source;

      const video = document.createElement("video");
      video.className = "scroll-scrub__video";
      video.muted = true;
      video.playsInline = true;
      video.preload = "auto";
      video.setAttribute("muted", "");
      video.setAttribute("playsinline", "");
      video.setAttribute("webkit-playsinline", "true");
      video.setAttribute("disableRemotePlayback", "");
      video.src = source;

      video.addEventListener(
        "loadedmetadata",
        () => {
          if (segment.video !== video || segment.loadedSource !== source) {
            return;
          }
          segment.ready = true;
          segment.loading = false;
          dirty = true;
        },
        { once: true }
      );
      video.addEventListener(
        "loadeddata",
        () => {
          if (
            userReady &&
            segment.video === video &&
            segment.loadedSource === source
          ) {
            void primeVideo(video);
          }
        },
        { once: true }
      );
      video.addEventListener(
        "error",
        () => {
          if (segment.video !== video) {
            return;
          }
          video.remove();
          delete segment.video;
          segment.failed = true;
          segment.loading = false;
          segment.ready = false;
          delete segment.layer.dataset.videoPainted;
          segment.layer.dataset.videoFailed = "true";
        },
        { once: true }
      );
      video.addEventListener("seeked", () => {
        if (segment.video === video && segment.loadedSource === source) {
          segment.isSeeking = false;
          if (!segment.layer.dataset.videoPainted) {
            segment.layer.dataset.videoPainted = "true";
          }
        }
      });

      segment.layer.append(video);
      segment.video = video;
    };

    const readScroll = () => {
      const pageY = window.scrollY || window.pageYOffset;
      const y = clamp(pageY - rootTop, 0, total);
      const crossfade = 0.1 * viewportHeight;
      let currentIndex = 0;

      for (const [index, segment] of runtime.entries()) {
        if (y >= segment.start) {
          currentIndex = index;
        }

        const length = Math.max(segment.end - segment.start, 1);
        const local = clamp((y - segment.start) / length);
        segment.target = segment.linger
          ? lingerEase(local, segment.linger)
          : local;

        const copyFade = segment.scene?.copyFade;
        if (copyFade) {
          const span = Math.max(copyFade[1] - copyFade[0], 0.001);
          const val = String(1 - clamp((local - copyFade[0]) / span));
          if (segment.lastCopy !== val) {
            segment.lastCopy = val;
            segment.band.style.setProperty("--ss-copy", val);
          }
        }
        if (segment.scene?.epilogue) {
          const at = segment.scene.epilogueAt ?? [0.7, 0.8, 0.9, 1];
          const fadeIn = clamp((local - at[0]) / Math.max(at[1] - at[0], 0.001));
          const fadeOut = clamp((local - at[2]) / Math.max(at[3] - at[2], 0.001));
          const val = String(fadeIn * (1 - fadeOut));
          if (segment.lastEpi !== val) {
            segment.lastEpi = val;
            segment.band.style.setProperty("--ss-epi", val);
          }
        }

        let outside = 0;
        if (y < segment.start) {
          outside = segment.start - y;
        }
        if (y > segment.end) {
          outside = y - segment.end;
        }
        let opacity = smoothstep(1 - outside / Math.max(crossfade, 1));
        if (reduceMotion) {
          opacity = outside === 0 ? 1 : 0;
        }

        segment.visible = opacity > 0.001;
        if (segment.lastOpacity === undefined || Math.abs(segment.lastOpacity - opacity) > 0.004) {
          segment.lastOpacity = opacity;
          segment.layer.style.opacity = String(opacity);
        }
        const newZ = index === currentIndex ? "2" : "1";
        if (segment.lastZ !== newZ) {
          segment.lastZ = newZ;
          segment.layer.style.zIndex = newZ;
        }

        if (!isMobile()) {
          if (canLoadClips) {
            const lookahead =
              index === 0 ? 1.5 * viewportHeight : 0.7 * viewportHeight;
            if (
              y > segment.start - lookahead &&
              y < segment.end + lookahead
            ) {
              loadClip(segment);
            }
          }
        }
      }

      if (isMobile() && canLoadClips) {
        const activeSeg = runtime[currentIndex];
        const sIdx = activeSeg?.sectionIndex ?? 0;
        manageMobileMemory(sIdx);
      }

      const current = runtime[currentIndex];
      const currentLength = Math.max(current.end - current.start, 1);
      const currentProgress = clamp((y - current.start) / currentLength);
      const nextActive =
        current.kind === "connector" && currentProgress >= 0.5
          ? current.nextSectionIndex
          : current.sectionIndex;

      if (nextActive !== active) {
        active = nextActive;
        root.dataset.activeSection = String(active);
        setActiveSection(active);
        onActiveRef.current?.(active);
      }

      const prog = clamp(y / total);
      if (Math.abs(lastProgress - prog) > 0.002) {
        lastProgress = prog;
        root.style.setProperty("--ss-progress", String(prog));
      }
    };

    // DESKTOP ONLY: Video seek throttle
    const updateVideos = () => {
      if (isMobile()) {
        return;
      }

      const now = performance.now();
      for (const segment of runtime) {
        const { video } = segment;
        if (!video || !segment.ready) {
          continue;
        }

        if (!segment.visible) {
          segment.current = segment.target;
          if (!video.paused) {
            video.pause();
          }
          continue;
        }

        const lerp = 0.32;
        segment.current += (segment.target - segment.current) * lerp;

        if (segment.isSeeking && segment.lastSeek && now - segment.lastSeek > 80) {
          segment.isSeeking = false;
        }

        const minSeekInterval = 16;
        if (video.seeking || segment.isSeeking) {
          continue;
        }
        if (segment.lastSeek && now - segment.lastSeek < minSeekInterval) {
          continue;
        }

        const targetTime =
          clamp(segment.current, 0, 0.999) * (video.duration || 1);
        const epsilon = 0.01;
        if (Math.abs(video.currentTime - targetTime) > epsilon) {
          segment.isSeeking = true;
          segment.lastSeek = now;
          try {
            video.currentTime = targetTime;
          } catch {
            segment.isSeeking = false;
          }
        }
      }
    };

    // MOBILE ONLY: Smooth 60 FPS lerp interpolation to eliminate all touch micro-lag
    const updateMobileLerp = () => {
      let isMoving = false;
      for (const seg of runtime) {
        if (seg.kind !== "scene") continue;
        if (seg.current === undefined) {
          seg.current = seg.target;
        }
        const diff = seg.target - seg.current;
        if (Math.abs(diff) > 0.0003) {
          seg.current += diff * 0.36; // Buttery 60 FPS lerp speed
          isMoving = true;
        } else if (seg.current !== seg.target) {
          seg.current = seg.target;
          isMoving = true;
        }
      }
      if (isMoving) {
        canvasDirty = true;
      }
    };

    const tick = () => {
      if (destroyed) {
        return;
      }
      if (dirty) {
        dirty = false;
        readScroll();
        canvasDirty = true;
      }
      if (isMobile()) {
        updateMobileLerp();
        renderCanvas();
      } else {
        updateVideos();
      }
      frame = window.requestAnimationFrame(tick);
    };

    const onScroll = () => {
      enableClips();
      dirty = true;
      canvasDirty = true;
    };
    const onResize = () => {
      if (coarsePointer && window.innerWidth === layoutWidth) {
        return;
      }
      layout();
      canvasDirty = true;
      if (isMobile()) {
        renderCanvas();
      }
    };
    const onFirstGesture = () => {
      enableClips();
      if (userReady) {
        return;
      }
      userReady = true;
      if (!isMobile()) {
        for (const segment of runtime) {
          void primeVideo(segment.video);
        }
      }
    };

    controllerRef.current = {
      jumpToSection(index) {
        const segment = runtime.find(
          (candidate) =>
            candidate.kind === "scene" && candidate.sectionIndex === index
        );
        if (!segment) {
          return;
        }
        const top =
          rootTop + segment.start + (isMobile() ? 0 : 0.15 * (segment.end - segment.start));
        window.scrollTo({
          behavior: reduceMotion ? "auto" : "smooth",
          top,
        });
      },
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", layout);
    window.addEventListener("pointerdown", onFirstGesture, {
      once: true,
      passive: true,
    });
    window.addEventListener("touchstart", onFirstGesture, {
      once: true,
      passive: true,
    });

    requestAnimationFrame(() => {
      layout();
      if (isMobile()) {
        renderCanvas();
      }
    });
    frame = window.requestAnimationFrame(tick);

    return () => {
      destroyed = true;
      controllerRef.current = null;
      window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", layout);
      window.removeEventListener("pointerdown", onFirstGesture);
      window.removeEventListener("touchstart", onFirstGesture);
      window.removeEventListener("touchstart", enableClips);
      window.removeEventListener("scroll", enableClips);
      window.removeEventListener("pointerdown", enableClips);
      root.style.removeProperty("--ss-progress");
      delete root.dataset.activeSection;

      for (const segment of runtime) {
        unloadClip(segment);
        segment.layer.style.removeProperty("opacity");
        segment.layer.style.removeProperty("z-index");
      }

      for (const [, img] of imageCache.entries()) {
        img.onload = null;
        img.onerror = null;
        img.src = "";
      }
      imageCache.clear();
      preloadedScenes.clear();
    };
  }, [segments]);

  if (scenes.length === 0) {
    return null;
  }

  const themeStyle: ThemeStyle = {
    "--ss-accent": theme.accent,
    "--ss-bg": theme.background,
    "--ss-ink": theme.ink,
    "--ss-muted": theme.muted,
  };

  return (
    <section
      className={["scroll-scrub", className].filter(Boolean).join(" ")}
      ref={rootRef}
      style={themeStyle}
    >
      <div className="scroll-scrub__stage">
        <div aria-hidden="true" className="scroll-scrub__media">
          {segments.map((segment, index) => {
            const layerStyle: ThemeStyle = {
              "--ss-mobile-position": segment.mobileObjectPosition,
              "--ss-object-position": segment.objectPosition,
            };
            return (
              <figure
                className={`scroll-scrub__layer scroll-scrub__layer--${segment.kind}`}
                data-scroll-scrub-layer=""
                key={segment.key}
                style={layerStyle}
              >
                <picture className="scroll-scrub__picture">
                  {segment.mobilePoster ? (
                    <source
                      media="(hover: none) and (pointer: coarse), (max-width: 860px)"
                      srcSet={segment.mobilePoster}
                    />
                  ) : null}
                  <img
                    alt=""
                    className="scroll-scrub__poster"
                    decoding="async"
                    fetchPriority={index === 0 ? "high" : "auto"}
                    loading={index === 0 ? "eager" : "lazy"}
                    src={segment.poster}
                    width={1280}
                    height={720}
                  />
                </picture>
              </figure>
            );
          })}
          <canvas className="scroll-scrub__canvas" ref={canvasRef} />
        </div>

        <div aria-hidden="true" className="scroll-scrub__progress">
          <span />
        </div>

        <nav aria-label={chaptersLabel} className="scroll-scrub__route">
          {scenes.map((scene, index) => (
            <button
              aria-current={activeSection === index ? "step" : undefined}
              className="scroll-scrub__route-button"
              key={scene.id}
              onClick={() => controllerRef.current?.jumpToSection(index)}
              type="button"
            >
              <span className="scroll-scrub__route-num">{String(index + 1).padStart(2, "0")}</span>
              <span className="scroll-scrub__route-sep" aria-hidden="true"> · </span>
              <span className="scroll-scrub__route-name">{scene.label}</span>
            </button>
          ))}
        </nav>
      </div>

      <div className="scroll-scrub__story">
        {segments.map((segment) => {
          const bandStyle = {
            "--ss-weight": Math.max(segment.weight, 0.2),
          } as CSSProperties;

          if (segment.kind === "connector") {
            return (
              <div
                aria-hidden="true"
                className="scroll-scrub__connector-band"
                data-scroll-scrub-band=""
                key={segment.key}
                style={bandStyle}
              />
            );
          }

          const { scene } = segment;
          if (!scene) {
            return null;
          }
          const Heading = segment.sectionIndex === 0 ? "h1" : "h2";

          return (
            <article
              className="scroll-scrub__chapter"
              data-align={scene.align ?? "left"}
              data-scroll-scrub-band=""
              id={scene.id}
              key={segment.key}
              style={bandStyle}
            >
              <div className="scroll-scrub__chapter-pin">
                <div className="scroll-scrub__copy">
                  {scene.kicker ? (
                    <p className="scroll-scrub__kicker">{scene.kicker}</p>
                  ) : null}
                  <Heading className="scroll-scrub__title">
                    {scene.title}
                  </Heading>
                  <p className="scroll-scrub__body">{scene.body}</p>
                  {scene.tags?.length ? (
                    <ul className="scroll-scrub__tags">
                      {scene.tags.map((tag) => (
                        <li key={tag}>{tag}</li>
                      ))}
                    </ul>
                  ) : null}
                  {scene.actions ? (
                    <div className="scroll-scrub__actions">{scene.actions}</div>
                  ) : null}
                </div>
                {scene.epilogue ? (
                  <p className="scroll-scrub__epilogue">
                    {scene.epilogue}
                  </p>
                ) : null}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
